/* import __COLOCATED_TEMPLATE__ from './map.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency-decorators';
import type ImportSource from 'embercom/models/import-service/import-source';
import { SourceType } from 'embercom/models/import-service/import-source';
import type { TaskGenerator } from 'ember-concurrency';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import { taskFor } from 'ember-concurrency-ts';
import { action } from '@ember/object';
import type IntlService from 'embercom/services/intl';
import { SyncBehaviorType } from 'embercom/models/import-service/import-source';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import type Store from '@ember-data/store';
interface Args {
  nextStep: () => void;
  previousStep: () => void;
  sourceId?: number;
  toggleSideDrawer: () => void;
}

interface IntercomFieldsStructure {
  required: string[];
  optional: string[];
  default: Record<string, string>;
}

interface FieldMapping {
  [key: string]: string[]; // intercom field : [source field]
}

export default class StandaloneSalesforceArticleSyncStepMap extends Component<Args> {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare intl: IntlService;
  @service declare intercomEventService: $TSFixMe;
  @service declare notificationsService: $TSFixMe;
  @service declare store: Store;
  @tracked mappingData: any[] = [];
  @tracked importSource: ImportSource | null = null;
  @tracked isConnecting = false;
  @tracked salesforceGroupList: any[] = [];
  @tracked optionalGroupList: any[] = [];
  @tracked metadata: any[] = [];
  @tracked intercomFields: any[] = [];
  @tracked requiredGroupList: any[] = [];
  @tracked fieldMap: FieldMapping = {};
  @tracked mappingComplete = false;

  constructor(owner: unknown, args: Args) {
    super(owner, args);

    taskFor(this.loadImportSourcesTask).perform();
  }

  get isLoading() {
    return taskFor(this.loadImportSourcesTask).isRunning;
  }

  get syncButtonLabel() {
    return this.args.sourceId
      ? this.intl.t('standalone.salesforce.setup.article-sync.steps.map.update-sync-button-label')
      : this.intl.t('standalone.salesforce.setup.article-sync.steps.map.sync-button-label');
  }

  @task({ drop: true })
  *loadImportSourcesTask(): TaskGenerator<void> {
    if (this.args.sourceId) {
      this.importSource = yield this.store.findRecord(
        'import-service/import-source',
        Number(this.args.sourceId),
      );
      if (this.importSource) {
        yield this.importSource.reload();
      }
    } else {
      let sources = yield this.knowledgeHubService.fetchImportSources();
      let sfSource = sources.find(
        (source: ImportSource) => source.sourceType === SourceType.Salesforce,
      );

      if (sfSource) {
        this.importSource = yield sfSource.reload();
      } else {
        try {
          this.importSource = yield this.knowledgeHubService.createImportSource({
            sourceType: SourceType.Salesforce,
          });
        } catch (error) {
          if (this.isSalesforceMetaDataError(error)) {
            this.notificationsService.notifyError(
              this.intl.t('standalone.salesforce.setup.article-sync.metadata-error'),
            );
          }
        }
      }
    }

    yield this.initiateFieldMapper();
  }

  @action
  async initiateFieldMapper() {
    if (!this.importSource) {
      throw new Error('Import source is not defined');
    }

    this.metadata = this.importSource.metadata;
    this.intercomFields = this.importSource.entityMetadata;

    let fields = this.intercomFields[0] as unknown as IntercomFieldsStructure;
    let defaultMappings = fields.default || {};
    let existingMappings = this.importSource.context?.mappings?.firstObject;

    let requiredFields = fields.required.map((intercomField) => {
      let salesforceField =
        existingMappings?.[intercomField]?.[0] || defaultMappings[intercomField];
      return this.createFieldData(intercomField, true, salesforceField);
    });

    this.mappingData = [...requiredFields];

    if (existingMappings) {
      let optionalFields = new Set(fields.optional);

      let existingOptionalMappings = Object.entries(existingMappings)
        .filter(([intercomField]) => optionalFields.has(intercomField))
        .flatMap(([intercomField, salesforceFields]: [string, string[]]) =>
          salesforceFields.map((salesforceField) => ({
            status: 'mapped',
            sourceFieldName: intercomField,
            sourceField: intercomField,
            destinationFieldName: salesforceField,
            destinationField: salesforceField,
            fieldType: 'string',
            readOnly: false,
            isRequired: false,
          })),
        );

      this.mappingData.push(...existingOptionalMappings);
    }

    this.fieldMap = this.createFieldMap();

    let sortedDefaultSalesforceFields = this.metadata
      .filter((field: any) => !field.name.endsWith('__c'))
      .map((field: any) => ({
        id: field.name,
        label: field.name,
        text: field.name,
        icon: this.itemIcon(field),
      }))
      .sort((a, b) => a.text.localeCompare(b.text));

    let sortedCustomSalesforceFields = this.metadata
      .filter((field: any) => field.name.endsWith('__c'))
      .map((field: any) => ({
        id: field.name,
        label: field.name,
        text: field.name,
        icon: this.itemIcon(field),
      }))
      .sort((a, b) => a.text.localeCompare(b.text));

    this.salesforceGroupList = [
      {
        heading: this.intl.t('standalone.salesforce.setup.article-sync.steps.map.dropdown-custom'),
        isFilterable: true,
        items: [...sortedCustomSalesforceFields],
      },
      {
        heading: this.intl.t(
          'standalone.salesforce.setup.article-sync.steps.map.dropdown-standard',
        ),
        isFilterable: true,
        items: [...sortedDefaultSalesforceFields],
      },
    ];

    this.requiredGroupList = [
      {
        items: fields.required.map((field: any) => ({
          id: field,
          label: field,
          text: field,
        })),
      },
    ];

    this.optionalGroupList = [
      {
        items: fields.optional.map((field: any) => ({
          text: field,
          value: field,
          component: 'standalone/salesforce/setup/article-sync/steps/dropdown-with-tooltip',
          componentAttrs: {
            text: field,
            hasTooltip: ['content', 'tags', 'public_url'].includes(field),
          },
        })),
      },
    ];
    this.checkMappingComplete();
  }

  @action
  createFieldMap() {
    this.fieldMap = this.mappingData.reduce((acc, mapping) => {
      if (mapping.status === 'mapped') {
        if (
          (mapping.sourceFieldName === 'content' || mapping.sourceFieldName === 'tags') &&
          acc[mapping.sourceFieldName]
        ) {
          acc[mapping.sourceFieldName].push(mapping.destinationField);
        } else {
          acc[mapping.sourceFieldName] = [mapping.destinationField];
        }
      }
      return acc;
    }, {} as FieldMapping);

    return this.fieldMap;
  }

  @action
  deleteMapping(mapping: any) {
    this.mappingData = this.mappingData.filter((m) => m !== mapping);
    this.fieldMap = this.createFieldMap();
    this.checkMappingComplete();
  }

  @action
  addSalesforceField(field?: any) {
    this.mappingData = [
      ...this.mappingData,
      {
        status: 'pending',
        sourceFieldName: this.intl.t(`knowledge-hub.sync-modal.field-mapper.field-placeholder`),
        sourceField: '',
        destinationFieldName:
          field?.label || this.intl.t(`knowledge-hub.sync-modal.field-mapper.field-placeholder`),
        destinationField: field?.id || '',
        fieldType: 'string',
        readOnly: false,
        isRequired: false,
      },
    ];

    this.fieldMap = this.createFieldMap();
    this.checkMappingComplete();
  }

  @action
  selectSalesforceItem(rowData: FieldMapping, selectedItem: any) {
    this.mappingData = this.mappingData.map((mapping) =>
      mapping === rowData
        ? {
            ...mapping,
            destinationField: selectedItem.id,
            destinationFieldName: selectedItem.label,
            status: mapping.sourceField !== '' ? 'mapped' : 'pending',
          }
        : mapping,
    );

    this.fieldMap = this.createFieldMap();
    this.checkMappingComplete();
  }

  @action
  selectIntercomItem(rowData: FieldMapping, selectedItem: any) {
    this.mappingData = this.mappingData.map((mapping) =>
      mapping === rowData
        ? {
            ...mapping,
            sourceField: selectedItem,
            sourceFieldName: selectedItem,
            status: 'mapped',
          }
        : mapping,
    );

    this.fieldMap = this.createFieldMap();
    this.checkMappingComplete();
  }

  get unmappedSalesforceFields() {
    return this.metadata.filter(
      (field: any) => !Object.values(this.fieldMap).flat().includes(field.name),
    ).length;
  }

  private get tableRowsAreValid() {
    let placeholder = this.intl.t(`knowledge-hub.sync-modal.field-mapper.field-placeholder`);

    return !this.mappingData.some((data) => {
      return data.sourceFieldName === placeholder || data.destinationFieldName === placeholder;
    });
  }

  private get requiredFieldsMapped() {
    let requiredFields = this.importSource?.entityMetadata[0].required || [];

    return requiredFields.every((field: string) => Object.keys(this.fieldMap).includes(field));
  }

  @action
  checkMappingComplete() {
    this.mappingComplete = this.requiredFieldsMapped && this.tableRowsAreValid;
  }

  @action
  async completeMapping() {
    if (!this.importSource) {
      return;
    }
    await this.knowledgeHubService.updateImportSourceWithParam(this.importSource.id, {
      mappings: this.fieldMap,
    });

    this.startImport();
    this.args.nextStep();
  }

  @action
  async startImport() {
    if (!this.importSource) {
      throw new Error('Import source is not defined');
    }

    await this.knowledgeHubService.startImport(this.importSource.id, SyncBehaviorType.Sync);

    this.knowledgeHubService.notifySourceStateChangeConfirmation(
      this.intl.t(`knowledge-hub.sync-modal.salesforce.sync-started`),
      this.importSource.id,
    );

    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      context: 'import-content',
      object: `import-content.salesforce`,
      section: `knowledge-hub.new-content.salesforce`,
      place: 'knowledge-hub.new-content',
    });
  }

  private createFieldData(field: string, isRequired: boolean, defaultMapping?: string) {
    let destinationField = defaultMapping || '';
    let destinationFieldName =
      defaultMapping || this.intl.t(`knowledge-hub.sync-modal.field-mapper.field-placeholder`);
    let status = defaultMapping ? 'mapped' : 'pending';

    return {
      status,
      sourceFieldName: field,
      sourceField: field,
      destinationFieldName,
      destinationField,
      fieldType: 'string',
      readOnly: false,
      isRequired,
    };
  }

  private isSalesforceMetaDataError(error: any): boolean {
    let payload = error.jqXHR?.responseJSON;
    return (
      payload &&
      payload.error?.type === 'MetadataNotFoundError' &&
      payload.error?.source === 'salesforce'
    );
  }

  private itemIcon(item: any): InterfaceIconName {
    switch (item.type) {
      case 'boolean':
      case 'bool':
        return 'boolean';
      case 'number':
      case 'int':
      case 'float':
        return 'numbers';
      case 'datetime':
      case 'date':
        return 'calendar';
      case 'picklist':
      case 'multipicklist':
        return 'list';
      default:
        return 'small-text';
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Salesforce::Setup::ArticleSync::Steps::Map': typeof StandaloneSalesforceArticleSyncStepMap;
    'standalone/salesforce/setup/article-sync/steps/map': typeof StandaloneSalesforceArticleSyncStepMap;
  }
}
